<template>
  <Layout
    :assetType="assetType"
    :asset="asset"
    active="send"
    :title="`Send to Counterparty | ${assetType.name}`"
  >
    <form @submit.prevent="submit" class="flex-grow-1 flex-column d-flex py-5 bg-light flex-shrink-0">
      <div class="flex-grow-1 flex-shrink-0">
        <div class="container">
          <div v-if="counterPartyUsers.length">
            <Typeahead
              class="form-group"
              v-model="counterPartyUser"
              label="Who would you like to send the contract to?"
              :items="counterPartyUsers"
              placeholder="Start typing to search..."
              :tolerance="0"
              id-field="id"
              :search-field="['fullname', 'email']"
              display-field="fullname"
              :errors="$page.props.errors.reviewer_name"
              @selected="counterPartyUserChosen"
            >
              <template v-slot:item="slotProps">
                <span v-if="slotProps.match.fullname">
                  {{ slotProps.match.fullname }}
                  <br />
                </span>
                <small v-if="slotProps.match.email">Email: {{ slotProps.match.email }}</small>
              </template>
              <template v-slot:noResults>
                <small>
                  We couldn't find a user with that name or email.
                </small>
              </template>
            </Typeahead>
          </div>

          <div v-else>
            <div class="form-group">
              <TextInput
                v-model="form.reviewer_email"
                :errors="$page.props.errors.reviewer_email"
                type="email"
                label="Email Address"
                placeholder="jane.doe@clausify.com"
                helper="We'll email your reviewer a link to the contract."
                autofocus
                autocapitalize="off"
                :disabled="isLocked"
                validateSuccess
                validateError
              />
            </div>

            <div class="form-group">
              <TextInput
                v-model="form.reviewer_name"
                :errors="$page.props.errors.reviewer_name"
                type="text"
                label="Reviewer's first name"
                placeholder="Jane"
                helper="This will appear in the email. For example, if the reviewer's name is &quot;Jane&quot; then the email will say, &quot;Hi Jane&quot;."
                autofocus
                autocapitalize="off"
                :disabled="isLocked"
                validateSuccess
                validateError
              />
            </div>
          </div>

          <Message v-model="form.reviewer_message" :name="form.reviewer_name" />

          <CcEmails
            :reviewerCC="form.reviewer_cc"
            @added="(value) => form.reviewer_cc.push(value)"
            @removed="(index) => form.reviewer_cc.splice(index, 1)"
          />
        </div>
      </div>

      <div class="mt-4 flex-shrink-0">
        <div class="container">
          <div class="row">
            <div class="col-md-4 offset-md-4">
              <LoadingButton :loading="sending" class="btn btn-block btn-primary" type="submit" :disabled="isLocked">
                Send to Counterparty
              </LoadingButton>
            </div>
          </div>
        </div>
      </div>
    </form>
  </Layout>
</template>

<script>
import helpers from '@/Mixins/helpers';
import contractState from '@/Mixins/contractState';
import Layout from './Layout';
import Typeahead from '@/Shared/Inputs/NewTypeahead';
import TextInput from '@/Shared/Inputs/Text';
import LoadingButton from '@/Shared/LoadingButton';
import Message from '@/Shared/Inputs/Message';
import CcEmails from '@/Shared/Inputs/CcEmails';

export default {
  mixins: [helpers, contractState],

  components: {
    Layout,
    Typeahead,
    TextInput,
    LoadingButton,
    Message,
    CcEmails,
  },

  props: {
    assetType: Object,
    asset: Object,
    counterPartyUsers: Array,
  },

  data() {
    const reviewerData = this.asset && this.asset.reviewer ? this.asset && this.asset.reviewer : [];

    return {
      form: this.$inertia.form({
        reviewer_email: reviewerData.reviewer_email ? reviewerData.reviewer_email : '',
        reviewer_name: reviewerData.reviewer_name ? reviewerData.reviewer_name : '',
        reviewer_message: reviewerData.reviewer_message ? reviewerData.reviewer_message : '',
        reviewer_cc: reviewerData.reviewer_cc ? reviewerData.reviewer_cc : [],

        username: `${this.$page.props.auth.user.first_name} ${this.$page.props.auth.user.last_name}`,
        counter_party: this.asset.counter_party,
        organisation: this.$page.props.auth.user.organisation,
      }),
      counterPartyUser: reviewerData.reviewer_email ? this.counterPartyUsers.find((user) => String(user.email) === String(reviewerData.reviewer_email)) : null,
      sending: false,
    };
  },

  mounted() {
    this.fetchAssetState(this.asset.id);

    if (this.asset.reviewer_email) {
      // eslint-disable-next-line max-len
      this.counterPartyUser = this.counterPartyUsers.find((user) => user.email === this.asset.reviewer_email);
    }
  },

  computed: {
    isLocked() {
      if (this.state.withCounterparty) {
        return true;
      }
      return this.state.state === 'editing';
    },
  },

  methods: {
    counterPartyUserChosen(counterParty) {
      this.$page.props.errors.reviewer_name = [];

      if (counterParty) {
        this.form.reviewer_name = counterParty.fullname;
        this.form.reviewer_email = counterParty.email;
      } else {
        this.form.reviewer_name = null;
        this.form.reviewer_email = null;
      }
    },

    submit() {
      this.$nextTick(() => {
        if (this.asset.state === 'editing' || this.state.withCounterparty) {
          return;
        }

        this.sending = true;

        this.form.put(this.route('asset.send.update', this.asset.id), {
          preserveScroll: true,
          onSuccess: () => {
            this.sending = false;
          },
        });

      });
    },
  },
};
</script>
